import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import moment, { Moment } from 'moment';
import { userState, vendorState } from 'state';
import {
  ProcessorSchedulingCalendarQuery,
  ProcessorSchedulingCalendarQueryVariables,
  useProcessorSchedulingCalendarLazyQuery,
  useUserSettingsByUserIdQuery,
} from '../../../lib/graphql';
import { CalendarLoading } from '@farmshare/ui-components';

/**
 * Calendar context type definition including start and end date management
 * with URL query parameter synchronization for persistent navigation state.
 */
interface CalendarContextType {
  startDate: Moment | null;
  endDate: Moment | null;
  currentDate: Moment | null;
  selectedDate: Date | undefined;
  calendarData: ProcessorSchedulingCalendarQuery | undefined;
  loadCalendarData: (
    variables: ProcessorSchedulingCalendarQueryVariables,
  ) => void;
  isLoadingCalendarData: boolean;
  viewFilters: string[];
  speciesFilters: string[];
  /**
   * IDs of the vendor's `CalendarEventType` rows the user has hidden.
   * Inverted on purpose: a newly created event type shows up on the calendar
   * without every saved URL / user preference having to be rewritten to
   * include it. Empty means "show every enabled type".
   */
  hiddenEventTypes: string[];
  currentView: string;
  showAll: boolean;
  setStartDate: (date: Moment | null) => void;
  setEndDate: (date: Moment | null) => void;
  setDateRange: (start: Moment | null, end: Moment | null) => void;
  setSelectedDate: (date: Date | undefined) => void;
  setViewFilters: (filters: string[] | ((prev: string[]) => string[])) => void;
  setSpeciesFilters: (
    filters: string[] | ((prev: string[]) => string[]),
  ) => void;
  setHiddenEventTypes: (
    hidden: string[] | ((prev: string[]) => string[]),
  ) => void;
  toggleEventTypeFilter: (eventTypeId: string) => void;
  setCurrentView: (view: string) => void;
  setShowAll: (showAll: boolean) => void;
  toggleViewFilter: (filter: string) => void;
  toggleSpeciesFilter: (species: string) => void;
  /**
   * `JobTag` ids to keep. EMPTY MEANS NO FILTER — same convention as
   * `speciesFilters`, and the opposite of `hiddenEventTypes`, which is
   * inverted so new event types appear without a migration.
   */
  tagFilters: string[];
  setTagFilters: (filters: string[] | ((prev: string[]) => string[])) => void;
  toggleTagFilter: (tagId: string) => void;
  viewFilterVariant: (filter: string) => 'contained' | 'outlined';
  isInitialLoad: boolean;
  hasCompletedInitialNavigation: boolean;
  setHasCompletedInitialNavigation: (completed: boolean) => void;
}

const CalendarContext = createContext<CalendarContextType | undefined>(
  undefined,
);

export const useCalendarContext = () => {
  const ctx = useContext(CalendarContext);
  if (!ctx)
    throw new Error('useCalendarContext must be used within CalendarProvider');
  return ctx;
};

/**
 * Calendar provider component that manages calendar state including start/end dates,
 * view filters, current view, and show all state with URL query parameter synchronization.
 * When users navigate away from the calendar and return, they will see the same
 * configuration they were viewing before.
 *
 * URL Parameters:
 * - startDate: ISO date string (YYYY-MM-DD) for the calendar start date
 * - endDate: ISO date string (YYYY-MM-DD) for the calendar end date
 * - viewFilters: Comma-separated list of active view filters
 * - currentView: The current calendar view (e.g., 'dayGridMonth', 'timeGridWeek')
 * - showAll: Boolean string ('true'/'false') for show all state
 * @param props - Component props
 * @param props.children - Child components to render within the calendar context
 * @returns JSX element providing calendar context to children
 */
export const CalendarProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useRecoilValue(userState);
  const vendor = useRecoilValue(vendorState);

  // Calendar data management
  const [loadCalendarDataQuery, processorSchedulingCalendarQueryResult] =
    useProcessorSchedulingCalendarLazyQuery({ fetchPolicy: 'network-only' });

  // Calendar data methods
  const loadCalendarData = useCallback(
    (variables: ProcessorSchedulingCalendarQueryVariables) => {
      loadCalendarDataQuery({ variables });
    },
    [loadCalendarDataQuery],
  );

  const calendarData = processorSchedulingCalendarQueryResult.data;
  // "Not asked yet" counts as loading. The query is fired from an effect, so
  // on the first render it is neither loading nor loaded — and the mobile
  // agenda used that frame to announce an empty day and drop the capacity rail
  // before the skeleton appeared.
  //
  // Only while there is a vendor to ask for, though. The query is never fired
  // without one, and "not asked yet" would then be the skeleton for good
  // instead of the empty day.
  const isLoadingCalendarData =
    processorSchedulingCalendarQueryResult.loading ||
    (!processorSchedulingCalendarQueryResult.called && !!vendor?._id);

  // Load user settings
  const { data: userSettingsData, loading: userSettingsLoading } =
    useUserSettingsByUserIdQuery({
      variables: { userId: user?._id },
      skip: !user?._id,
    });

  // Initialize state with defaults first
  const [viewFilters, setViewFiltersState] = useState<string[]>([
    'dropOffDate',
  ]);
  const [speciesFilters, setSpeciesFiltersState] = useState<string[]>([]);
  const [tagFilters, setTagFiltersState] = useState<string[]>([]);
  const [hiddenEventTypes, setHiddenEventTypesState] = useState<string[]>(
    () => {
      const urlParams = new URLSearchParams(window.location.search);
      return (urlParams.get('hiddenEventTypes') ?? '')
        .split(',')
        .filter(Boolean);
    },
  );
  const [currentView, setCurrentViewState] = useState<string>('dayGridMonth');
  const [showAll, setShowAllState] = useState<boolean>(false);
  const [hasInitialized, setHasInitialized] = useState<boolean>(false);

  // Initialize dates from URL params synchronously to prevent race conditions
  const [startDate, setStartDateState] = useState<Moment | null>(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const startParam = urlParams.get('startDate');
    return startParam && moment(startParam).isValid()
      ? moment(startParam)
      : null;
  });
  const [endDate, setEndDateState] = useState<Moment | null>(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const endParam = urlParams.get('endDate');
    return endParam && moment(endParam).isValid() ? moment(endParam) : null;
  });

  // Initialize selectedDate from URL params synchronously
  const [selectedDate, setSelectedDateState] = useState<Date | undefined>(
    () => {
      const urlParams = new URLSearchParams(window.location.search);
      const selectedParam = urlParams.get('selectedDate');
      return selectedParam && moment(selectedParam).isValid()
        ? moment(selectedParam).toDate()
        : undefined;
    },
  );

  // Flag to prevent FullCalendar from overwriting URL dates during initial load
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  // Flag to track if we've completed navigation to URL dates
  const [hasCompletedInitialNavigation, setHasCompletedInitialNavigation] =
    useState<boolean>(false);

  // Initialize state from URL params and user settings once when data is available
  useEffect(() => {
    // Only initialize once
    if (hasInitialized) {
      return;
    }

    // Wait for user settings to load if we have a user
    if (user?._id && userSettingsLoading) {
      return;
    }

    const currentSearchParams = new URLSearchParams(window.location.search);
    const viewFiltersParam = currentSearchParams.get('viewFilters');
    const speciesFiltersParam = currentSearchParams.get('speciesFilters');
    const tagFiltersParam = currentSearchParams.get('tagFilters');
    const currentViewParam = currentSearchParams.get('currentView');
    const showAllParam = currentSearchParams.get('showAll');

    // Set view filters: URL param > user settings > default
    if (viewFiltersParam) {
      setViewFiltersState(viewFiltersParam.split(',').filter(Boolean));
    } else if (userSettingsData) {
      const calendarDateConfig = userSettingsData.userSettingsByUserId?.settings
        ?.calendar?.dateConfiguration as string[] | undefined;
      if (calendarDateConfig) {
        setViewFiltersState(calendarDateConfig);
      }
    }

    // Set species filters from URL param (empty = show all species)
    if (speciesFiltersParam) {
      setSpeciesFiltersState(speciesFiltersParam.split(',').filter(Boolean));
    }

    if (tagFiltersParam) {
      setTagFiltersState(tagFiltersParam.split(',').filter(Boolean));
    }

    // Set current view: URL param > user settings > default (URL param always wins)
    if (currentViewParam) {
      setCurrentViewState(currentViewParam);
    } else if (userSettingsData && !currentViewParam) {
      const calendarView =
        userSettingsData.userSettingsByUserId?.settings?.calendar?.calendarView;
      if (calendarView) {
        switch (calendarView) {
          case 'list':
            setCurrentViewState('mobileView');
            break;
          case 'week':
            setCurrentViewState('dayGridWeek');
            break;
          default:
            setCurrentViewState('dayGridMonth');
        }
      }
    }

    // Set show all: URL param > user settings > default
    if (showAllParam !== null) {
      setShowAllState(showAllParam === 'true');
    } else if (userSettingsData) {
      const showAllEvents =
        userSettingsData.userSettingsByUserId?.settings?.calendar
          ?.showAllEvents;
      if (showAllEvents !== undefined && showAllEvents !== null) {
        setShowAllState(showAllEvents);
      }
    }

    setHasInitialized(true);

    // Allow FullCalendar to update dates after initial load is complete
    // Use a longer timeout to ensure FullCalendar finishes its initial rendering
    // and our navigation has time to complete
    setTimeout(() => {
      setIsInitialLoad(false);
    }, 100);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSettingsLoading, hasInitialized, user?._id]);

  // URL-synchronized setters for calendar state
  const setViewFilters = useCallback(
    (filters: string[] | ((prev: string[]) => string[])) => {
      const newFilters =
        typeof filters === 'function' ? filters(viewFilters) : filters;

      // Early return if the filters haven't actually changed
      if (JSON.stringify(viewFilters) === JSON.stringify(newFilters)) {
        return;
      }

      setViewFiltersState(newFilters);
      const newSearchParams = new URLSearchParams(searchParams);
      if (newFilters.length > 0) {
        newSearchParams.set('viewFilters', newFilters.join(','));
      } else {
        newSearchParams.delete('viewFilters');
      }
      setSearchParams(newSearchParams, { replace: true });
    },
    [searchParams, setSearchParams, viewFilters],
  );

  const setSpeciesFilters = useCallback(
    (filters: string[] | ((prev: string[]) => string[])) => {
      const newFilters =
        typeof filters === 'function' ? filters(speciesFilters) : filters;

      if (JSON.stringify(speciesFilters) === JSON.stringify(newFilters)) {
        return;
      }

      setSpeciesFiltersState(newFilters);
      const newSearchParams = new URLSearchParams(searchParams);
      if (newFilters.length > 0) {
        newSearchParams.set('speciesFilters', newFilters.join(','));
      } else {
        newSearchParams.delete('speciesFilters');
      }
      setSearchParams(newSearchParams, { replace: true });
    },
    [searchParams, setSearchParams, speciesFilters],
  );

  const setTagFilters = useCallback(
    (filters: string[] | ((prev: string[]) => string[])) => {
      const newFilters =
        typeof filters === 'function' ? filters(tagFilters) : filters;

      if (JSON.stringify(tagFilters) === JSON.stringify(newFilters)) {
        return;
      }

      setTagFiltersState(newFilters);
      const newSearchParams = new URLSearchParams(searchParams);
      if (newFilters.length > 0) {
        newSearchParams.set('tagFilters', newFilters.join(','));
      } else {
        newSearchParams.delete('tagFilters');
      }
      setSearchParams(newSearchParams, { replace: true });
    },
    [searchParams, setSearchParams, tagFilters],
  );

  const setHiddenEventTypes = useCallback(
    (hidden: string[] | ((prev: string[]) => string[])) => {
      const next =
        typeof hidden === 'function' ? hidden(hiddenEventTypes) : hidden;

      if (JSON.stringify(hiddenEventTypes) === JSON.stringify(next)) {
        return;
      }

      setHiddenEventTypesState(next);
      const newSearchParams = new URLSearchParams(searchParams);
      if (next.length > 0) {
        newSearchParams.set('hiddenEventTypes', next.join(','));
      } else {
        newSearchParams.delete('hiddenEventTypes');
      }
      setSearchParams(newSearchParams, { replace: true });
    },
    [searchParams, setSearchParams, hiddenEventTypes],
  );

  const toggleEventTypeFilter = useCallback(
    (eventTypeId: string) => {
      setHiddenEventTypes((prev: string[]) =>
        prev.includes(eventTypeId)
          ? prev.filter((id) => id !== eventTypeId)
          : [...prev, eventTypeId],
      );
    },
    [setHiddenEventTypes],
  );

  const toggleSpeciesFilter = useCallback(
    (species: string) => {
      setSpeciesFilters((prev: string[]) =>
        prev.includes(species)
          ? prev.filter((s) => s !== species)
          : [...prev, species],
      );
    },
    [setSpeciesFilters],
  );

  const toggleTagFilter = useCallback(
    (tagId: string) => {
      setTagFilters((prev: string[]) =>
        prev.includes(tagId)
          ? prev.filter((id) => id !== tagId)
          : [...prev, tagId],
      );
    },
    [setTagFilters],
  );

  const setCurrentView = useCallback(
    (view: string) => {
      // Early return if the view hasn't actually changed
      if (currentView === view) {
        return;
      }

      setCurrentViewState(view);
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('currentView', view);
      setSearchParams(newSearchParams, { replace: true });
    },
    [searchParams, setSearchParams, currentView],
  );

  const setShowAll = useCallback(
    (show: boolean) => {
      // Early return if the value hasn't actually changed
      if (showAll === show) {
        return;
      }

      setShowAllState(show);
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('showAll', show.toString());
      setSearchParams(newSearchParams, { replace: true });
    },
    [searchParams, setSearchParams, showAll],
  );

  // Update URL params when dates change - with safety checks
  const setStartDate = useCallback(
    (date: Moment | null) => {
      // Check if the date is actually different to prevent unnecessary updates
      const isSameDate =
        startDate?.isSame(date, 'day') ?? (startDate === null && date === null);

      // Early return if the date hasn't actually changed
      if (isSameDate) {
        return;
      }

      // Skip all updates during initial load to prevent overwriting URL-loaded dates
      if (isInitialLoad) {
        return;
      }

      setStartDateState(date);

      // Always update the URL parameter for the start date
      // Use current URL to ensure we have the latest params
      const newSearchParams = new URLSearchParams(window.location.search);
      if (date) {
        newSearchParams.set('startDate', date.format('YYYY-MM-DD'));
      } else {
        newSearchParams.delete('startDate');
      }
      setSearchParams(newSearchParams, { replace: true });
    },
    [setSearchParams, startDate, isInitialLoad],
  );

  const setEndDate = useCallback(
    (date: Moment | null) => {
      // Check if the date is actually different to prevent unnecessary updates
      const isSameDate =
        endDate?.isSame(date, 'day') ?? (endDate === null && date === null);

      // Early return if the date hasn't actually changed
      if (isSameDate) {
        return;
      }

      // Skip all updates during initial load to prevent overwriting URL-loaded dates
      if (isInitialLoad) {
        return;
      }

      setEndDateState(date);

      // Always update the URL parameter for the end date
      // Use current URL to ensure we have the latest params
      const newSearchParams = new URLSearchParams(window.location.search);
      if (date) {
        newSearchParams.set('endDate', date.format('YYYY-MM-DD'));
      } else {
        newSearchParams.delete('endDate');
      }
      setSearchParams(newSearchParams, { replace: true });
    },
    [setSearchParams, endDate, isInitialLoad],
  );

  // URL-synchronized setter for selectedDate
  const setSelectedDate = useCallback(
    (date: Date | undefined) => {
      // Check if the date is actually different to prevent unnecessary updates
      const isSameDate =
        selectedDate && date
          ? moment(selectedDate).isSame(moment(date), 'day')
          : selectedDate === date;

      // Early return if the date hasn't actually changed
      if (isSameDate) {
        return;
      }

      // Skip all updates during initial load to prevent overwriting URL-loaded dates
      if (isInitialLoad) {
        return;
      }

      setSelectedDateState(date);

      // Always update the URL parameter for the selected date
      // Use current URL to ensure we have the latest params
      const newSearchParams = new URLSearchParams(window.location.search);
      if (date) {
        newSearchParams.set('selectedDate', moment(date).format('YYYY-MM-DD'));
      } else {
        newSearchParams.delete('selectedDate');
      }
      setSearchParams(newSearchParams, { replace: true });
    },
    [setSearchParams, selectedDate, isInitialLoad],
  );

  // Helper function to set both dates together safely
  const setDateRange = useCallback(
    (start: Moment | null, end: Moment | null) => {
      setStartDateState(start);
      setEndDateState(end);

      const newSearchParams = new URLSearchParams(searchParams);
      if (start && end) {
        newSearchParams.set('startDate', start.format('YYYY-MM-DD'));
        newSearchParams.set('endDate', end.format('YYYY-MM-DD'));
      } else {
        newSearchParams.delete('startDate');
        newSearchParams.delete('endDate');
      }
      setSearchParams(newSearchParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const toggleViewFilter = useCallback(
    (filter: string) => {
      setViewFilters((prev: string[]) =>
        prev.includes(filter)
          ? prev.filter((f) => f !== filter)
          : [...prev, filter],
      );
    },
    [setViewFilters],
  );

  // Determine button variant based on active filters
  const viewFilterVariant = useCallback(
    (filter: string) => {
      if (viewFilters.includes(filter)) {
        return 'contained';
      }

      return 'outlined';
    },
    [viewFilters],
  );

  // Calculate current date as exactly halfway between start and end dates
  const currentDate = React.useMemo(() => {
    if (!startDate || !endDate) {
      return null;
    }

    const startMs = startDate.valueOf();
    const endMs = endDate.valueOf();
    const midpointMs = startMs + (endMs - startMs) / 2;

    return moment(midpointMs);
  }, [startDate, endDate]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = React.useMemo(
    () => ({
      viewFilters,
      setViewFilters,
      speciesFilters,
      setSpeciesFilters,
      tagFilters,
      setTagFilters,
      toggleTagFilter,
      toggleSpeciesFilter,
      hiddenEventTypes,
      setHiddenEventTypes,
      toggleEventTypeFilter,
      currentView,
      setCurrentView,
      showAll,
      setShowAll,
      toggleViewFilter,
      viewFilterVariant,
      startDate,
      setStartDate,
      endDate,
      setEndDate,
      currentDate,
      selectedDate,
      setSelectedDate,
      calendarData,
      loadCalendarData,
      isLoadingCalendarData,
      setDateRange,
      isInitialLoad,
      hasCompletedInitialNavigation,
      setHasCompletedInitialNavigation,
    }),
    [
      viewFilters,
      setViewFilters,
      speciesFilters,
      setSpeciesFilters,
      tagFilters,
      setTagFilters,
      toggleTagFilter,
      toggleSpeciesFilter,
      hiddenEventTypes,
      setHiddenEventTypes,
      toggleEventTypeFilter,
      currentView,
      setCurrentView,
      showAll,
      setShowAll,
      toggleViewFilter,
      viewFilterVariant,
      startDate,
      setStartDate,
      endDate,
      setEndDate,
      currentDate,
      selectedDate,
      setSelectedDate,
      calendarData,
      loadCalendarData,
      isLoadingCalendarData,
      setDateRange,
      isInitialLoad,
      hasCompletedInitialNavigation,
      setHasCompletedInitialNavigation,
    ],
  );

  // Hold the calendar back until the saved view has been applied. Gating on
  // `hasInitialized` rather than on the settings query matters: the effect
  // that applies the settings runs a render after they arrive, and in that one
  // render the calendar would mount on the defaults — then jump to the saved
  // view and "show all" density, resizing every row.
  if (!hasInitialized) {
    return <CalendarLoading />;
  }

  return (
    <CalendarContext.Provider value={contextValue}>
      {children}
    </CalendarContext.Provider>
  );
};
